import { computed, ref } from "vue";
import { useCountDown, useLoading } from "@sa/hooks";
import { useAppStore } from "@/store/modules/app";
import { $t } from "@/locales";
import { REG_PHONE } from "@/constants/reg";
import { addPhone } from "@/service/api/auth";

export function useCaptcha() {
  const { loading, startLoading, endLoading } = useLoading();
  const { count, start, stop, isCounting } = useCountDown(60);
  const appStore = useAppStore();

  // Track fallback OTP provider when SMS service is unavailable
  const fallbackOtpProvider = ref<string | undefined>(undefined);
  const captchaError = ref("");
  const duplicatePhoneConflict = ref(false);

  const label = computed(() => {
    let text = $t("page.login.codeLogin.getCode");

    const countingLabel = $t("page.login.codeLogin.reGetCode", {
      time: count.value,
    });

    if (loading.value) {
      text = "";
    }

    if (isCounting.value) {
      text = countingLabel;
    }

    return text;
  });

  function isPhoneValid(phone: string) {
    if (phone.trim() === "") {
      captchaError.value = $t("form.phone.required");
      window.$message?.error?.(captchaError.value, {
        duration: 5000,
        closable: true,
        keepAliveOnHover: true,
      });

      return false;
    }

    if (!REG_PHONE.test(phone)) {
      captchaError.value = $t("form.phone.invalid");
      window.$message?.error?.(captchaError.value, {
        duration: 5000,
        closable: true,
        keepAliveOnHover: true,
      });

      return false;
    }

    return true;
  }

  /**
   * Get the display name for a fallback provider
   */
  function getFallbackProviderName(provider: string): string {
    return $t(`page.login.codeLogin.fallbackProvider.${provider}`) || provider;
  }

  /**
   * Show appropriate success message based on whether fallback provider was used
   */
  function showErrorMessage(content: string, duration = 7000) {
    captchaError.value = content;
    window.$message?.error?.(content, {
      duration,
      closable: true,
      keepAliveOnHover: true,
    });
  }

  function showSuccessMessage(provider: string | undefined) {
    const content = provider
      ? $t("page.login.codeLogin.sendCodeSuccessViaFallback", {
        provider: getFallbackProviderName(provider),
      })
      : $t("page.login.codeLogin.sendCodeSuccess");

    window.$message?.success?.(content, {
      duration: 5000,
      closable: true,
      keepAliveOnHover: true,
    });
  }

  async function getCaptcha(phone: string) {
    captchaError.value = "";
    duplicatePhoneConflict.value = false;

    const valid = isPhoneValid(phone);

    if (!valid || loading.value) {
      return { success: false, statusCode: valid ? undefined : 400 };
    }

    startLoading();

    // Reset fallback provider on new request
    fallbackOtpProvider.value = undefined;

    try {
      const reponse = await addPhone(phone);
      // console.log('reponse.error.response', reponse);
      if (reponse) {
      // Use statusCode from response (available for both success and error cases)
      const statusCode = reponse.statusCode ?? reponse.error?.response?.status;

      // Extract fallback_otp_provider from response data if present
      if (reponse.data?.fallback_otp_provider) {
        fallbackOtpProvider.value = reponse.data.fallback_otp_provider;
      }
      
      // Check for specific error status codes FIRST before checking for generic success
      // Status 226 means phone number is already associated with another account
      if (statusCode === 226) {
        duplicatePhoneConflict.value = true;
        captchaError.value = $t("page.login.codeLogin.phoneAlreadyAssociated");
        return { success: false, statusCode };
      } else if (statusCode === 429) {
        // Rate limit exceeded - show user-friendly message
        showErrorMessage($t("billing.tooManyRequests"));
        return { success: false, statusCode };
      } else if (statusCode === 403) {
        showErrorMessage($t("page.login.common.needLogin"));
        return { success: false, statusCode };
      } else if (statusCode === 201) {
        showSuccessMessage(fallbackOtpProvider.value);
        start();
        return { success: true, statusCode };
      } else if (statusCode === 200) {
        showSuccessMessage(fallbackOtpProvider.value);
        return { success: true, statusCode };
      } else if (!reponse.error && reponse.data) {
        // Generic success for other 2xx status codes
        window.$message?.success?.($t("common.phoneAlreadyVerified"), {
          duration: 5000,
          closable: true,
          keepAliveOnHover: true,
        });
        setTimeout(() => {
          appStore.reloadPage();
        }, 1200);
        return { success: true, statusCode };
      } else {
        showErrorMessage($t("page.login.codeLogin.sendFailed"));
        return { success: false, statusCode };
      }
      } else {
        showErrorMessage($t("page.login.codeLogin.sendFailed"));
        return { success: false };
      }
    } catch (error: any) {
      const statusCode = error?.response?.status ?? error?.statusCode;
      if (statusCode === 226) {
        duplicatePhoneConflict.value = true;
        captchaError.value = $t("page.login.codeLogin.phoneAlreadyAssociated");
        return { success: false, statusCode };
      }

      if (statusCode === 429) {
        showErrorMessage($t("billing.tooManyRequests"));
        return { success: false, statusCode };
      }

      if (statusCode === 403) {
        showErrorMessage($t("page.login.common.needLogin"));
        return { success: false, statusCode };
      }

      showErrorMessage($t("page.login.codeLogin.sendFailed"));
      return { success: false, statusCode };
    } finally {
      endLoading();
    }
  }

  return {
    label,
    start,
    stop,
    isCounting,
    loading,
    getCaptcha,
    fallbackOtpProvider,
    captchaError,
    duplicatePhoneConflict,
  };
}
